import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Users, User, Phone, MapPin } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface ClassSchedule {
  id: string;
  day_of_week: number;
  time_slot: string;
  court_number: number;
  max_students: number;
  instructor: {
    id: string;
    name: string;
    avatar_url?: string;
  } | null;
  level: {
    id: string;
    name: string;
    color: string;
  } | null;
  bookings_count: number;
}

interface ClassBooking {
  id: string;
  student_name: string;
  student_phone?: string;
  status: string;
  created_at: string;
}

const DAYS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];
const TIME_SLOTS = ['07:00', '08:00', '09:00', '16:00', '17:00'];

export default function ClassCalendar() {
  const [schedules, setSchedules] = useState<ClassSchedule[]>([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedClass, setSelectedClass] = useState<ClassSchedule | null>(null);
  const [classBookings, setClassBookings] = useState<ClassBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [bookingForm, setBookingForm] = useState({
    student_name: '',
    student_phone: ''
  });

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    try {
      const { data, error } = await supabase
        .from('class_schedules')
        .select(`
          *,
          instructor:instructors(id, name, avatar_url),
          level:class_levels(id, name, color)
        `)
        .eq('is_active', true)
        .order('day_of_week')
        .order('time_slot');

      if (error) throw error;

      // Contar reservas para cada horario
      const schedulesWithCounts = await Promise.all(
        data.map(async (schedule) => {
          const { count } = await supabase
            .from('class_bookings')
            .select('*', { count: 'exact' })
            .eq('schedule_id', schedule.id)
            .eq('booking_date', formatDate(selectedDate))
            .eq('status', 'confirmed');

          return {
            ...schedule,
            bookings_count: count || 0
          };
        })
      );

      setSchedules(schedulesWithCounts);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching schedules:', error);
      setLoading(false);
    }
  };

  const fetchClassBookings = async (scheduleId: string) => {
    try {
      const { data, error } = await supabase
        .from('class_bookings')
        .select('*')
        .eq('schedule_id', scheduleId)
        .eq('booking_date', formatDate(selectedDate))
        .eq('status', 'confirmed')
        .order('created_at');

      if (error) throw error;
      setClassBookings(data || []);
    } catch (error) {
      console.error('Error fetching class bookings:', error);
    }
  };

  const handleClassSelect = (schedule: ClassSchedule) => {
    setSelectedClass(schedule);
    fetchClassBookings(schedule.id);
  };

  const handleBookClass = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedClass) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        alert('Debes iniciar sesión para reservar una clase. La gestión de clases es pública, pero las reservas requieren autenticación.');
        return;
      }

      const { error } = await supabase
        .from('class_bookings')
        .insert([{
          schedule_id: selectedClass.id,
          user_id: user.id,
          student_name: bookingForm.student_name,
          student_phone: bookingForm.student_phone,
          booking_date: formatDate(selectedDate)
        }]);

      if (error) throw error;

      alert('¡Clase reservada exitosamente!');
      setBookingForm({ student_name: '', student_phone: '' });
      setSelectedClass(null);
      fetchSchedules(); // Actualizar conteos
    } catch (error: any) {
      alert('Error al reservar la clase: ' + error.message);
    }
  };

  const formatDate = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const formatTime = (time: string) => {
    return time.slice(0, 5);
  };

  const getAvailableSpots = (schedule: ClassSchedule) => {
    return schedule.max_students - schedule.bookings_count;
  };

  const canBookClass = (schedule: ClassSchedule) => {
    const classDate = new Date(selectedDate);
    const [hours, minutes] = schedule.time_slot.split(':');
    classDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    
    const now = new Date();
    const twoHoursFromNow = new Date(now.getTime() + 2 * 60 * 60 * 1000);
    
    return classDate > twoHoursFromNow && getAvailableSpots(schedule) > 0;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Reservar Clases</h2>
        <div className="flex items-center space-x-4">
          <Calendar className="h-5 w-5 text-gray-500" />
          <input
            type="date"
            value={formatDate(selectedDate)}
            onChange={(e) => setSelectedDate(new Date(e.target.value))}
            min={formatDate(new Date())}
            className="border border-gray-300 rounded-md px-3 py-2"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calendario de Clases */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Clases Disponibles</h3>
          <div className="space-y-4">
            {DAYS.map((day, dayIndex) => (
              <div key={day} className="border rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">{day}</h4>
                <div className="grid grid-cols-1 gap-2">
                  {TIME_SLOTS.map((timeSlot) => {
                    const daySchedules = schedules.filter(
                      s => s.day_of_week === dayIndex + 1 && s.time_slot === timeSlot + ':00'
                    );

                    return (
                      <div key={timeSlot} className="space-y-2">
                        <div className="text-sm font-medium text-gray-600">{timeSlot}</div>
                        {daySchedules.map((schedule) => (
                          <div
                            key={schedule.id}
                            onClick={() => handleClassSelect(schedule)}
                            className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                              selectedClass?.id === schedule.id
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <MapPin className="h-4 w-4 text-gray-500" />
                                <span className="text-sm font-medium">Cancha {schedule.court_number}</span>
                                {schedule.level && (
                                  <span
                                    className="px-2 py-1 text-xs rounded-full text-white"
                                    style={{ backgroundColor: schedule.level.color }}
                                  >
                                    {schedule.level.name}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center space-x-2">
                                <Users className="h-4 w-4 text-gray-500" />
                                <span className="text-sm font-medium">
                                  {getAvailableSpots(schedule)}/{schedule.max_students}
                                </span>
                                {getAvailableSpots(schedule) === 0 && (
                                  <span className="text-xs text-red-600 font-medium">LLENO</span>
                                )}
                              </div>
                            </div>
                            {schedule.instructor && (
                              <div className="flex items-center space-x-2 mt-2">
                                <User className="h-4 w-4 text-gray-500" />
                                <span className="text-sm text-gray-600">
                                  {schedule.instructor.name}
                                </span>
                              </div>
                            )}
                            {!schedule.instructor && (
                              <div className="flex items-center space-x-2 mt-2">
                                <User className="h-4 w-4 text-gray-400" />
                                <span className="text-sm text-gray-400 italic">
                                  Sin instructor asignado
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Panel de Reserva */}
        <div>
          {selectedClass ? (
            <div className="border rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-4">Reservar Clase</h3>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <span>{DAYS[selectedClass.day_of_week - 1]} - {formatTime(selectedClass.time_slot)}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MapPin className="h-4 w-4 text-gray-500" />
                  <span>Cancha {selectedClass.court_number}</span>
                </div>
                {selectedClass.level && (
                  <div className="flex items-center space-x-2">
                    <span
                      className="px-2 py-1 text-xs rounded-full text-white"
                      style={{ backgroundColor: selectedClass.level.color }}
                    >
                      {selectedClass.level.name}
                    </span>
                  </div>
                )}
                {selectedClass.instructor && (
                  <div className="flex items-center space-x-2">
                    <User className="h-4 w-4 text-gray-500" />
                    <span>Instructor: {selectedClass.instructor.name}</span>
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4 text-gray-500" />
                  <span>Espacios disponibles: {getAvailableSpots(selectedClass)}</span>
                </div>
              </div>

              {canBookClass(selectedClass) ? (
                <form onSubmit={handleBookClass} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre del Alumno
                    </label>
                    <input
                      type="text"
                      required
                      value={bookingForm.student_name}
                      onChange={(e) => setBookingForm({
                        ...bookingForm,
                        student_name: e.target.value
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Teléfono (Opcional)
                    </label>
                    <input
                      type="tel"
                      value={bookingForm.student_phone}
                      onChange={(e) => setBookingForm({
                        ...bookingForm,
                        student_phone: e.target.value
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Reservar Clase
                  </button>
                </form>
              ) : (
                <div className="text-center py-4">
                  <p className="text-gray-600">
                    {getAvailableSpots(selectedClass) === 0 
                      ? 'No hay espacios disponibles'
                      : 'No se puede reservar (menos de 2 horas de anticipación)'
                    }
                  </p>
                </div>
              )}

              {/* Lista de Alumnos Inscritos */}
              {classBookings.length > 0 && (
                <div className="mt-6 pt-6 border-t">
                  <h4 className="font-medium text-gray-900 mb-3">Alumnos Inscritos</h4>
                  <div className="space-y-2">
                    {classBookings.map((booking) => (
                      <div key={booking.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm">{booking.student_name}</span>
                        {booking.student_phone && (
                          <span className="text-sm text-gray-600">{booking.student_phone}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="border rounded-lg p-8 text-center text-gray-500">
              <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Selecciona una clase para ver los detalles y reservar</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}